import {fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/query/react";
import { updateIsRefreshTokenExpired, updateIsSessionExpired } from "../commonSlice";
import { logError } from "../../util/telemetry/error/log-error.util";


const rawBaseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
    prepareHeaders: (header) => {
        header.set("Accept", "application/json");
        return header;
    },
});

export const baseQueryWithHttpStatus: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    
    // Extract telemetry metadata
    const requestUrl = result.meta?.request?.url;
    const correlationId = result.meta?.response?.headers?.get('x_error') 
      || result.meta?.response?.headers?.get('cf-ray') 
      || result.meta?.response?.headers?.get('x-correlation-id') 
      || undefined;

    // Handle HTTP errors centrally
    if (result.error) {
        const status = result.error.status;
        const errorData = result.error.data;
        
        let errorString = 'Unknown Error';
        if ('error' in result.error) {
            errorString = String(result.error.error);
        } else if (errorData && typeof errorData === 'object') {
            const data = errorData as Record<string, unknown>;
            if (typeof data.message === 'string') {
                errorString = data.message;
            } else if (typeof data.error === 'string') {
                errorString = data.error;
            }
        } else if (typeof errorData === 'string') {
            errorString = errorData.substring(0, 250);
        }

        if (status === 401) {
            api.dispatch(updateIsSessionExpired(true));
            logError('Session Expired (401)', { category: 'auth_session', status, url: requestUrl });
        } else if (status === 403) {
            api.dispatch(updateIsRefreshTokenExpired(true));
            logError('Refresh Token Expired (403)', { category: 'auth_session', status, url: requestUrl });
        } else if (status === 'PARSING_ERROR') {
            logError(`API Parsing Error: ${errorString}`, { category: 'api_contract', correlationId, url: requestUrl });
        } else if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
            logError(`Network Error: ${errorString}`, { category: 'api_network', status: undefined, url: requestUrl });
        } else if (typeof status === 'number') {
            // General 4xx/5xx HTTP errors
            logError(`HTTP Error ${status}`, { category: 'api_http', status, correlationId, url: requestUrl });
        } else {
            // Fallback for custom/unknown RTK errors
            logError(`Unknown API Error: ${errorString}`, { category: 'api_http', correlationId, url: requestUrl });
        }
    }

    if (result.data !== undefined) {
        // Handle sneaky "successful" HTTP 200 responses that are actually business logic failures
        // Example: { success: false, statusCode: 404 }
        if (typeof result.data === 'object' && result.data !== null && 'success' in result.data) {
            const dataObj = result.data as Record<string, unknown>;
            if (dataObj.success === false) {
                const msg = typeof dataObj.message === 'string' ? dataObj.message : 'Unknown business failure';
                const code = dataObj.statusCode ?? dataObj.errorCode ?? 'N/A';
                logError(`API Business Error: [${code}] ${msg}`, { category: 'api_business', correlationId, url: requestUrl });
            }
        }


        // Safe check to see if the response is a standard JSON object
        const isObject = typeof result.data === "object" && result.data !== null && !Array.isArray(result.data);

        return {
            data: isObject 
                ? {
                    ...(result.data as object),
                    _meta: {
                        httpStatus: result.meta?.response?.status ?? 200,
                    }
                }
                : {
                    payload: result.data,
                    _meta: {
                        httpStatus: result.meta?.response?.status ?? 200,
                    }
                }
        };
    }

  return result;
};